import React, { lazy } from 'react';
import { motion } from 'framer-motion';
import { Check, Star } from 'lucide-react';
const benefits = ['Tightness easing instead of being overridden', 'Muscles relaxing instead of bracing', 'Comfort that builds instead of fading', 'Skin that feels normal, not coated', 'Relief that fits daily life, not emergencies only'];
const testimonials = [{
  text: "By night, my feet don't feel swollen anymore.",
  detail: "I'm on my feet all day. With GINEXT, that pressure just doesn't pile up the same way.",
  author: 'Mark R., 46',
  rating: 5
}, {
  text: 'No shock. No residue. Just warmth my body responds to.',
  detail: 'It absorbs fast and actually feels supportive.',
  author: 'Elena M., 38',
  rating: 5
}, {
  text: 'I thought foot pain meant overuse.',
  detail: 'It was pressure, not injury. This finally made sense.',
  author: 'Denise L., 54',
  rating: 5
}, {
  text: 'My ankles feel lighter by the end of the day.',
  detail: 'It fits into my routine because it feels normal.',
  author: 'Sarah K., 41',
  rating: 5
}];
interface BenefitsSectionProps {
  beforeImage?: string;
  afterImage?: string;
}
export function BenefitsSection({
  beforeImage,
  afterImage
}: BenefitsSectionProps = {}) {
  const defaultBeforeImage = 'https://cdn.magicpatterns.com/uploads/m5iHDaPB3eqSAFcbFGLXmt/u8896349413_Photorealistic_upper-body_image_of_an_adult_perso_aab59e0f-4ac0-416b-987f-cf6fcdf8216d_0.png';
  const defaultAfterImage = 'https://cdn.magicpatterns.com/uploads/dJqRnasfgaF65nvNGpkqVv/u8896349413_Photorealistic_upper-body_image_of_an_adult_perso_7ee71337-597c-4cf5-a59c-a9256b07140e_2.png';
  return <>
      <section className="py-8 sm:py-12 bg-charcoal text-bone" data-id="element-82">
        <div className="max-w-6xl mx-auto px-4" data-id="element-83">
          {/* Header Section */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 lg:gap-12 items-center mb-8 sm:mb-12" data-id="element-84">
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true,
            margin: '-50px'
          }} transition={{
            duration: 0.6
          }} data-id="element-85">
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-light mb-4 sm:mb-6 tracking-tight" data-id="element-86">
                WHAT YOU'LL FEEL (AND WHAT YOU WON'T)
              </h2>
              <div className="w-12 sm:w-16 h-px bg-herbal/30 mb-4 sm:mb-6" data-id="element-87" />
              <p className="text-sm sm:text-base text-gray-400 leading-relaxed font-light" data-id="element-88">
                You won't feel an icy blast. You won't feel aggressive tingling.
                You'll feel warmth arrive gradually, then deepen.
              </p>
            </motion.div>

            <div className="space-y-3 sm:space-y-4" data-id="element-89">
              {benefits.map((benefit, index) => <motion.div key={index} initial={{
              opacity: 0,
              x: 30
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true,
              margin: '-50px'
            }} transition={{
              duration: 0.4,
              delay: index * 0.05
            }} className="flex items-start gap-3" data-id="element-90">
                  <div className="mt-1 w-5 h-5 rounded-full bg-herbal/20 flex items-center justify-center flex-shrink-0" data-id="element-91">
                    <Check className="w-3 h-3 text-herbal" data-id="element-92" />
                  </div>
                  <p className="text-sm sm:text-base font-light" data-id="element-93">{benefit}</p>
                </motion.div>)}
            </div>
          </div>

          {/* Before/After Images - Added Here */}
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-50px'
        }} transition={{
          duration: 0.6,
          delay: 0.2
        }} className="max-w-4xl mx-auto" data-id="element-94">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8" data-id="element-95">
              {/* Before Image */}
              <div className="relative" data-id="element-96">
                <p className="text-gray-400 text-sm sm:text-base font-light mb-3 text-center" data-id="element-97">
                  Before GINEXT
                </p>
                <div className="relative rounded-2xl overflow-hidden shadow-2xl" data-id="element-98">
                  <img src={beforeImage || defaultBeforeImage} alt="Before GINEXT use" className="w-full h-auto" loading="lazy" width="600" height="800" data-id="element-99" />
                  {/* Warm glow overlay effect */}
                  <div className="absolute inset-0 bg-gradient-to-tr from-ginger/20 via-transparent to-transparent mix-blend-overlay pointer-events-none" data-id="element-100" />
                </div>
              </div>

              {/* After Image */}
              <div className="relative" data-id="element-101">
                <p className="text-herbal text-sm sm:text-base font-normal mb-3 text-center" data-id="element-102">
                  After GINEXT use
                </p>
                <div className="relative rounded-2xl overflow-hidden shadow-2xl" data-id="element-103">
                  <img src={afterImage || defaultAfterImage} alt="Neck and shoulders relaxed and comfortable after GINEXT use" className="w-full h-auto" loading="lazy" width="600" height="800" data-id="element-104" />
                  {/* Warm glow overlay effect */}
                </div>

                {/* Floating badge */}
                <div className="absolute bottom-4 left-4 bg-white/50 backdrop-blur-md p-2 sm:p-2.5 rounded-lg shadow-md border border-bone-dark/40 max-w-[100px] sm:max-w-[120px]" data-id="element-105">
                  <p className="text-ginger font-normal text-xs sm:text-sm leading-tight" data-id="element-106">
                    100% Natural
                  </p>
                  <p className="text-charcoal text-[10px] sm:text-xs font-light" data-id="element-107">
                    Zero synthetics
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-6 sm:py-8 bg-bone" data-id="element-108">
        <div className="max-w-6xl mx-auto px-4" data-id="element-109">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-50px'
        }} transition={{
          duration: 0.6
        }} className="text-center mb-6 sm:mb-8" data-id="element-110">
            <h2 className="text-xl sm:text-2xl md:text-3xl font-light text-charcoal mb-2 tracking-tight" data-id="element-111">
              "THIS DOESN'T FEEL LIKE PAIN CREAM"
            </h2>
            <p className="text-sm sm:text-base text-charcoal-light font-light" data-id="element-112">
              People don't switch back because freezing tired feet stops making
              sense.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4" data-id="element-113">
            {testimonials.map((testimonial, index) => <motion.div key={index} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-50px'
          }} transition={{
            duration: 0.5,
            delay: index * 0.1
          }} className="bg-white rounded-lg p-3 sm:p-4 shadow-md border border-bone-dark" data-id="element-114">
                {/* Star Rating */}
                <div className="flex gap-0.5 mb-2" data-id="element-115">
                  {[...Array(testimonial.rating)].map((_, i) => <Star key={i} className="w-3 h-3 fill-ginger text-ginger" data-id="element-116" />)}
                </div>

                {/* Quote */}
                <p className="text-sm sm:text-base font-normal text-charcoal mb-2" data-id="element-117">
                  "{testimonial.text}"
                </p>

                {/* Detail */}
                <p className="text-xs text-charcoal-light leading-relaxed mb-2 font-light" data-id="element-118">
                  {testimonial.detail}
                </p>

                {/* Author */}
                <p className="text-xs font-normal text-ginger" data-id="element-119">
                  {testimonial.author}
                </p>
              </motion.div>)}
          </div>
        </div>
      </section>
    </>;
}